import { useLingui } from '@lingui/react/macro';
import {
  IconDeviceDesktop,
  IconLanguage,
  IconMoon,
  IconPlus,
  IconSun,
} from 'app-ui/icon';
import { useNavigate } from 'react-router-dom';

import { type CommandMenuCommand } from '@/command-menu/types/CommandMenuCommand';
import { LOCALE_NATIVE_NAMES } from '@/localization/constants/LocaleNativeNames';
import { switchLocale } from '@/localization/utils/switchLocale';
import { NAVIGATION_ITEMS } from '@/navigation/constants/NavigationItems';
import { useSettingsNavigationSections } from '@/navigation/hooks/useSettingsNavigationSections';
import { useOpenInterventionSidePanel } from '@/intervention/hooks/useOpenInterventionSidePanel';
import { useHasPermission } from '@/session/hooks/useHasPermission';
import { colorSchemePreferenceState } from '@/ui/theme/states/colorSchemePreferenceState';
import { useAtomState } from '@/ui/utilities/state/jotai/hooks/useAtomState';

export const useCommandMenuCommands = (): CommandMenuCommand[] => {
  const { t, i18n } = useLingui();
  const settingsNavigationSections = useSettingsNavigationSections();
  const navigate = useNavigate();
  const [colorSchemePreference, setColorSchemePreference] = useAtomState(
    colorSchemePreferenceState,
  );
  const canEditPlanning = useHasPermission('org:planning:edit');
  const { openInterventionSidePanel } = useOpenInterventionSidePanel();

  const mainNavigationCommands: CommandMenuCommand[] = NAVIGATION_ITEMS.map(
    (navigationItem) => ({
      id: `navigate-${navigationItem.id}`,
      group: 'navigation',
      label: i18n._(navigationItem.label),
      Icon: navigationItem.Icon,
      hotkey: navigationItem.goToHotkey,
      onSelect: () => {
        void navigate(navigationItem.path);
      },
    }),
  );

  const settingsNavigationCommands: CommandMenuCommand[] =
    settingsNavigationSections.flatMap((navigationSection) =>
      navigationSection.items.map((navigationItem) => ({
        id: `navigate-${navigationItem.id}`,
        group: 'navigation',
        label: i18n._(navigationItem.label),
        description: i18n._(navigationSection.title),
        Icon: navigationItem.Icon,
        keywords: [i18n._(navigationSection.title), t`Settings`],
        onSelect: () => {
          void navigate(navigationItem.path);
        },
      })),
    );

  const colorSchemeCommands: CommandMenuCommand[] = [
    {
      id: 'color-scheme-light',
      group: 'actions',
      label: t`Switch to light theme`,
      Icon: IconSun,
      keywords: [t`Appearance`],
      isCurrent: colorSchemePreference === 'light',
      onSelect: () => setColorSchemePreference('light'),
    },
    {
      id: 'color-scheme-dark',
      group: 'actions',
      label: t`Switch to dark theme`,
      Icon: IconMoon,
      keywords: [t`Appearance`],
      isCurrent: colorSchemePreference === 'dark',
      onSelect: () => setColorSchemePreference('dark'),
    },
    {
      id: 'color-scheme-system',
      group: 'actions',
      label: t`Switch to system theme`,
      Icon: IconDeviceDesktop,
      keywords: [t`Appearance`],
      isCurrent: colorSchemePreference === 'system',
      onSelect: () => setColorSchemePreference('system'),
    },
  ];

  const localeCommands: CommandMenuCommand[] = [
    {
      id: 'locale-en',
      group: 'actions',
      label: t`Switch to English`,
      Icon: IconLanguage,
      keywords: [LOCALE_NATIVE_NAMES.en, t`Language`],
      isCurrent: i18n.locale === 'en',
      onSelect: () => {
        void switchLocale('en');
      },
    },
    {
      id: 'locale-fr',
      group: 'actions',
      label: t`Switch to French`,
      Icon: IconLanguage,
      keywords: [LOCALE_NATIVE_NAMES.fr, t`Language`],
      isCurrent: i18n.locale === 'fr',
      onSelect: () => {
        void switchLocale('fr');
      },
    },
  ];

  return [
    ...mainNavigationCommands,
    ...settingsNavigationCommands,
    ...(canEditPlanning
      ? [
          {
            id: 'create-intervention',
            group: 'actions' as const,
            label: t`Create intervention`,
            Icon: IconPlus,
            onSelect: () => openInterventionSidePanel(),
          },
        ]
      : []),
    ...colorSchemeCommands,
    ...localeCommands,
  ];
};
